import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import {
  formatInterval,
  humanizeTimestamp,
  StatusIndicator,
} from "@/components/playlist/PlayListItem";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useDataTable } from "@/hooks/use-data-table";
import { useScheduledPlayNotifications } from "@/hooks/useScheduledPlayNotifications";
import { formatLocationType } from "@/lib/format";
import {
  getPollingOneTimePlays,
  getPollingPlays,
  Play,
  useDeletePlay,
} from "@/queries/plays";
import { useReportContext } from "@/stores/ReportContext";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Activity,
  ChevronRight,
  Clock,
  MapPin,
  Settings2,
  Tag,
  Target,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreateAdHocPlayButton } from "./CreateAdHocPlayButton";
import { UpdateAdHocPlayDialog } from "./UpdateAdHocPlayDialog";

const getStatusOrder = (play: Play) => {
  if (play.completed_at) return 3;
  if (play.actioned_at) return 2;
  if (play.scheduled_at || play.override_scheduled_at) return 1;
  return 0;
};

function RemovePlayButton({ play }: { play: Play }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const deletePlayMutation = useDeletePlay(play.id);

  const handleDeletePlay = async () => {
    try {
      await deletePlayMutation.mutateAsync();

      // Invalidate and refetch plays
      queryClient.invalidateQueries({ queryKey: ["plays"] });
      toast({
        title: "Success",
        description: `Play "${play.title}" has been deleted successfully`,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to delete play",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={deletePlayMutation.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Play</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the play{" "}
            <strong>"{play.title}"</strong>? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeletePlay}
            disabled={deletePlayMutation.isPending}
          >
            {deletePlayMutation.isPending ? "Deleting..." : "Delete Play"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PlayListTasks = () => {
  const { reportId } = useReportContext();

  const { data, isSuccess } = getPollingPlays(reportId, {});
  const { data: oneTimePlaysData, isSuccess: oneTimeIsSuccess } =
    getPollingOneTimePlays({});
  const plays = [...(data?.plays || []), ...(oneTimePlaysData?.plays || [])];

  // Initialize notification checking for scheduled plays
  useScheduledPlayNotifications(isSuccess ? data.plays : []);

  // Only render when both queries have succeeded and data exists
  const canRender =
    isSuccess && oneTimeIsSuccess && data?.plays && oneTimePlaysData?.plays;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "start_interval",
    direction: "asc",
  });

  // Helper functions to manage URL parameters
  const getBooleanParam = (
    key: string,
    defaultValue: boolean = false
  ): boolean => {
    const value = searchParams.get(key);
    return value === null ? defaultValue : value === "true";
  };

  const setBooleanParam = (key: string, value: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, "true");
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // URL-based filter states
  const showCompleted = getBooleanParam("showCompleted", false);
  const showScheduled = getBooleanParam("showScheduled", false);
  const showOneTime = getBooleanParam("showOneTime", false);
  const showIgnored = getBooleanParam("showIgnored", false);
  const onlyShowUpcoming = getBooleanParam("onlyShowUpcoming", false);

  // Setter functions for URL-based states
  const setShowCompleted = (value: boolean) =>
    setBooleanParam("showCompleted", value);
  const setShowScheduled = (value: boolean) =>
    setBooleanParam("showScheduled", value);
  const setShowOneTime = (value: boolean) =>
    setBooleanParam("showOneTime", value);
  const setShowIgnored = (value: boolean) =>
    setBooleanParam("showIgnored", value);
  const setOnlyShowUpcoming = (value: boolean) =>
    setBooleanParam("onlyShowUpcoming", value);

  // Handle row click to navigate to task detail
  const handleRowClick = (play: Play) => {
    navigate(`/plays/${play.id}`);
  };

  // Get current interval (time of day in 10-min intervals)
  const currentInterval = useMemo(() => {
    const now = new Date();
    return Math.floor((now.getHours() * 60 + now.getMinutes()) / 10);
  }, []);

  // Generate dynamic location options from actual data
  const locationOptions = useMemo(() => {
    const uniqueLocations = Array.from(
      new Set(plays.map((play) => play.location_type))
    ).sort();

    return uniqueLocations.map((location) => ({
      label: formatLocationType(location),
      value: location,
    }));
  }, [plays]);

  // Filter and sort the data manually
  const processedData = useMemo(() => {
    let filteredPlays = plays.slice();

    // Apply custom filters
    if (showCompleted) {
      filteredPlays = filteredPlays.filter((play) => play.completed_at);
    }

    if (showScheduled) {
      filteredPlays = filteredPlays.filter(
        (play) => play.override_scheduled_at
      );
    }

    if (showOneTime) {
      filteredPlays = filteredPlays.filter((play) => play.adhoc);
    }

    if (!showIgnored) {
      filteredPlays = filteredPlays.filter((play) => !play.auto_resolved_at);
    }

    if (onlyShowUpcoming) {
      filteredPlays = filteredPlays.filter(
        (play) => play.start_interval >= currentInterval
      );
    }

    // Apply location filter
    if (locationFilter.length > 0) {
      filteredPlays = filteredPlays.filter((play) =>
        locationFilter.includes(play.location_type)
      );
    }

    // Apply sorting
    filteredPlays.sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.key) {
        case "start_interval":
          comparison = a.start_interval - b.start_interval;
          break;

        case "title":
          comparison = a.title.localeCompare(b.title);
          break;

        case "location_type":
        case "location":
          comparison = a.location_type.localeCompare(b.location_type);
          break;

        case "observed_threshold":
          // Put adhoc plays at the end
          if (a.adhoc && !b.adhoc) return 1;
          if (!a.adhoc && b.adhoc) return -1;
          if (a.adhoc && b.adhoc) return 0;

          // Sort by observed value for threshold plays
          const aObserved = a.observed_value || 0;
          const bObserved = b.observed_value || 0;
          comparison = aObserved - bObserved;
          break;

        case "status":
          // Custom status sorting: pending -> scheduled -> actioned -> completed
          comparison = getStatusOrder(a) - getStatusOrder(b);
          break;

        case "category":
          comparison = a.category.localeCompare(b.category);
          break;
      }

      return sortConfig.direction === "desc" ? -comparison : comparison;
    });

    return filteredPlays;
  }, [
    plays,
    locationFilter,
    sortConfig,
    showCompleted,
    showScheduled,
    showOneTime,
    showIgnored,
    onlyShowUpcoming,
    currentInterval,
  ]);

  const columns = useMemo<ColumnDef<Play>[]>(
    () => [
      {
        id: "start_interval",
        accessorKey: "start_interval",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Time" />
        ),
        cell: ({ getValue }) => {
          const interval = getValue() as number;
          return (
            <div className="font-mono text-slate-300 opacity-80 flex items-center gap-2">
              <div>{formatInterval(interval)}</div>
              <div className="opacity-50 text-xs">
                ({humanizeTimestamp(interval)})
              </div>
            </div>
          );
        },
        meta: {
          label: "Time",
          icon: Clock,
        },
        enableSorting: true,
      },
      {
        id: "title",
        accessorKey: "title",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Title" />
        ),
        cell: ({ getValue }) => (
          <span className="font-semibold">{getValue() as string}</span>
        ),
        meta: {
          label: "Title",
        },
        enableSorting: true,
      },
      {
        id: "location",
        accessorKey: "location_type",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Location" />
        ),
        cell: ({ row }) => (
          <Badge variant="secondary">
            {row.original.location_type === "other"
              ? ""
              : formatLocationType(row.original.location_type)}
            {row.original.location_details &&
              ` ${row.original.location_details}`}
          </Badge>
        ),
        meta: {
          label: "Location",
          variant: "multiSelect",
          icon: MapPin,
          options: locationOptions,
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValues) => {
          if (
            !filterValues ||
            !Array.isArray(filterValues) ||
            filterValues.length === 0
          ) {
            return true;
          }
          const locationValue = row.getValue(columnId) as string;
          return filterValues.includes(locationValue);
        },
      },
      {
        id: "observed_threshold",
        accessorFn: (row) =>
          !row.adhoc ? `${row.observed_value}/${row.threshold_value}` : "adhoc",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Observed/Threshold" />
        ),
        cell: ({ row }) => {
          if (row.original.adhoc) {
            return (
              <Badge
                variant="default"
                className="bg-green-700 hover:bg-green-700 text-white uppercase"
              >
                One-Time
              </Badge>
            );
          }
          return (
            <Badge variant="secondary" className="uppercase gap-1">
              <span>{row.original.observed_value} pax</span>
              <ChevronRight className="w-3 h-3" />
              <span>{row.original.threshold_value}</span>
            </Badge>
          );
        },
        meta: {
          label: "Observed/Threshold",
          icon: Target,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const aAdhoc = rowA.original.adhoc;
          const bAdhoc = rowB.original.adhoc;

          // Put adhoc plays at the end
          if (aAdhoc && !bAdhoc) return 1;
          if (!aAdhoc && bAdhoc) return -1;
          if (aAdhoc && bAdhoc) return 0;

          // Sort by observed value for threshold plays
          const aObserved = rowA.original.observed_value || 0;
          const bObserved = rowB.original.observed_value || 0;
          return aObserved - bObserved;
        },
      },
      {
        id: "status",
        accessorFn: (row) => {
          if (row.completed_at) return "completed";
          if (row.actioned_at) return "actioned";
          if (row.scheduled_at || row.override_scheduled_at) return "scheduled";
          return "pending";
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <div className="flex space-x-4">
            <StatusIndicator
              label="Schedule"
              timestamp={row.original.scheduled_at}
              override_timestamp={row.original.override_scheduled_at}
            />
            <StatusIndicator
              label="Execute"
              timestamp={row.original.actioned_at}
            />
            <StatusIndicator
              label="Complete"
              timestamp={row.original.completed_at}
            />
          </div>
        ),
        meta: {
          label: "Status",
          icon: Activity,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          // Custom status sorting: pending -> scheduled -> actioned -> completed
          const statusOrder = {
            pending: 0,
            scheduled: 1,
            actioned: 2,
            completed: 3,
          };
          const aStatus = rowA.getValue("status") as keyof typeof statusOrder;
          const bStatus = rowB.getValue("status") as keyof typeof statusOrder;
          return statusOrder[aStatus] - statusOrder[bStatus];
        },
      },
      {
        id: "category",
        accessorKey: "category",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Category" />
        ),
        cell: ({ getValue }) => (
          <Badge variant="outline" className="uppercase">
            {getValue() as string}
          </Badge>
        ),
        meta: {
          label: "Category",
          icon: Tag,
        },
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const play = row.original;
          return (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {play.adhoc ? (
                <>
                  {!play.completed_at && <UpdateAdHocPlayDialog play={play} />}
                  <RemovePlayButton play={play} />
                </>
              ) : (
                <Button
                  variant="outline"
                  size="icon"
                  className="invisible"
                  disabled
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [locationOptions]
  );

  const { table } = useDataTable({
    data: processedData,
    columns,
    pageCount: 1,
    onSortingChangeCallback: (sorting) => {
      if (sorting.length > 0) {
        const firstSort = sorting[0];
        setSortConfig({
          key: firstSort.id,
          direction: firstSort.desc ? "desc" : "asc",
        });
      }
    },
    onColumnFiltersChangeCallback: (columnFilters) => {
      const locationFilter = columnFilters.find(
        (filter) => filter.id === "location"
      );
      if (locationFilter && Array.isArray(locationFilter.value)) {
        setLocationFilter(locationFilter.value);
      } else if (!locationFilter) {
        setLocationFilter([]);
      }
    },
  });

  if (!canRender) return null;

  return (
    <DataTable
      table={table}
      hidePagination
      cellClassName="py-3"
      onRowClick={handleRowClick}
    >
      <DataTableToolbar table={table}>
        <CreateAdHocPlayButton />

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 border-dashed">
              <Settings2 className="mr-2 h-4 w-4" />
              Display Options
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <div className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="show-completed"
                    className="text-sm font-medium"
                  >
                    Show Completed Only
                  </Label>
                  <Switch
                    id="show-completed"
                    checked={showCompleted}
                    onCheckedChange={setShowCompleted}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="show-scheduled"
                    className="text-sm font-medium"
                  >
                    Show Scheduled Only
                  </Label>
                  <Switch
                    id="show-scheduled"
                    checked={showScheduled}
                    onCheckedChange={setShowScheduled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="show-one-time"
                    className="text-sm font-medium"
                  >
                    Show One-Time Only
                  </Label>
                  <Switch
                    id="show-one-time"
                    checked={showOneTime}
                    onCheckedChange={setShowOneTime}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-ignored" className="text-sm font-medium">
                    Show Ignored
                  </Label>
                  <Switch
                    id="show-ignored"
                    checked={showIgnored}
                    onCheckedChange={setShowIgnored}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="only-upcoming"
                    className="text-sm font-medium"
                  >
                    Only Upcoming
                  </Label>
                  <Switch
                    id="only-upcoming"
                    checked={onlyShowUpcoming}
                    onCheckedChange={setOnlyShowUpcoming}
                  />
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </DataTableToolbar>
    </DataTable>
  );
};

export default PlayListTasks;
